import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { LogIn, RefreshCw, AlertCircle, CheckCircle, ShieldCheck, X } from 'lucide-react';

const APPS_URL = '/.netlify/functions/appsscript';
const ROSTER_URL = '/.netlify/functions/roster-data';

// Managers who can authorise a no-log-on.
const AUTHORISERS = ['BOUVENGP2', 'BOUVENGJ', 'SOTOCRUZL'];

const REASONS = [
  'Sick Leave',
  'Holiday Leave',
  'Not Rostered',
  'RDO / Day Off',
  'Unscheduled Leave',
  'Unexplained Leave',
  'Worked — Did Not Log On',
  'Vehicle Breakdown',
  'Other',
];

// Authorising with one of these reasons ALSO writes an approved Leave Calendar entry
// for the day, so it tallies on the driver's profile (sick days). Reasons not listed
// here (Not Rostered, RDO, Vehicle Breakdown, …) are absences we don't count as leave.
const REASON_LEAVE_TYPE = {
  'Sick Leave': 'sick',
};
// Log-On Check area codes → Leave Calendar team names.
const AREA_TO_TEAM = {
  CITY: 'City', DARL: 'Darlinghurst', BWAY: 'Broadway',
  DARLINGHURST: 'Darlinghurst', BROADWAY: 'Broadway',
};
function leaveUid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 7); }

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function LoginCheckPage() {
  const { currentUser } = useApp();
  const login = (currentUser?.login || '').toUpperCase();
  const canAuthorise = AUTHORISERS.includes(login);

  // ?date= deep link from the global search palette opens that day's board.
  const [searchParams] = useSearchParams();
  const dateParam = /^\d{4}-\d{2}-\d{2}$/.test(searchParams.get('date') || '') ? searchParams.get('date') : '';
  const [date, setDate]       = useState(dateParam || todayISO());
  useEffect(() => { if (dateParam) setDate(dateParam); }, [dateParam]);
  const [data, setData]       = useState(null);
  const [roster, setRoster]   = useState([]);   // Leave Calendar entries
  const [notRostered, setNotRosteredList] = useState([]); // standing "usually not rostered" list
  const [standing, setStanding] = useState(false); // "Not Rostered": also add to the standing list?
  const [addNR, setAddNR]     = useState('');   // driver being added to the Not Rostered list
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState('');
  const [editing, setEditing] = useState(null);   // login being authorised
  const [reason, setReason]   = useState(REASONS[0]);
  const [note, setNote]       = useState('');
  const [saving, setSaving]   = useState(false);
  const [reopening, setReopening] = useState(''); // login being reopened
  const [syncing, setSyncing]     = useState(''); // login whose sick day we're tallying
  const [starting, setStarting]   = useState(null); // login whose shift we're starting
  const [startTime, setStartTime] = useState('');
  const [startKm, setStartKm]     = useState('');
  const [startBusy, setStartBusy] = useState(false);
  const nowHHMM = () => { const d = new Date(); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
  const fmtDay = (iso) => { if (!iso) return ''; const d = new Date(`${iso}T12:00:00`); return isNaN(d) ? iso : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }); };
  const leaveKind = (t) => t === 'sick' ? 'Sick' : t === 'exception' ? 'Leave' : 'Holiday';

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      // Log-on status and the Leave Calendar in parallel. The roster read is
      // best-effort — a failure must not stop the log-on check from rendering.
      const [res, rosterRes, nrRes] = await Promise.all([
        fetch(APPS_URL, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'getLoginCheck', date }),
        }),
        fetch(ROSTER_URL).catch(() => null),
        fetch('/.netlify/functions/not-rostered-sb', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'getNotRostered' }),
        }).catch(() => null),
      ]);
      const d = await res.json();
      if (!d.success) throw new Error(d.error || 'Could not load log-on check');
      setData(d);
      try {
        const rd = rosterRes ? await rosterRes.json() : null;
        setRoster(rd && Array.isArray(rd.entries) ? rd.entries : []);
      } catch { setRoster([]); }
      try {
        const nr = nrRes ? await nrRes.json() : null;
        setNotRosteredList(nr && Array.isArray(nr.list) ? nr.list : []);
      } catch { setNotRosteredList([]); }
    } catch (e) { setErr(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  // Keep the Leave Calendar in step with a sick authorisation: ensure exactly one
  // approved entry (tagged source:'login-check') covering this date for the driver,
  // or remove ours when they're no longer marked as leave. The calendar is the system
  // of record, so this is what makes the day tally on their profile. Best-effort — a
  // failure here must not undo the authorisation itself. `type` is null to remove.
  const syncAbsenceLeave = async (driverLogin, type) => {
    let full;
    try { full = await (await fetch(ROSTER_URL)).json(); } catch { return; }
    const entries = Array.isArray(full?.entries) ? full.entries : [];
    const events  = Array.isArray(full?.events)  ? full.events  : [];
    if (!entries.length && !events.length) return; // empty read = glitch; never clobber the roster

    const isMine = e => (e.login || '').toUpperCase() === driverLogin && e.startDate === date && e.source === 'login-check';
    let next = entries.filter(e => !isMine(e)); // drop any prior auto-entry for this driver+day

    if (type) {
      // Don't double-count if an approved entry of this type already covers the date.
      const covered = next.some(e =>
        (e.login || '').toUpperCase() === driverLogin && e.status === 'approved' &&
        e.type === type && e.startDate <= date && e.endDate >= date);
      if (!covered) {
        const row = (data?.rows || []).find(r => r.login === driverLogin) || {};
        next = [...next, {
          id: leaveUid(), login: driverLogin, name: row.name || driverLogin,
          team: AREA_TO_TEAM[(row.area || '').toUpperCase()] || '',
          type, startDate: date, endDate: date, status: 'approved',
          notes: `Marked absent (Sick Leave) at Log-On Check by ${login}`,
          source: 'login-check', createdAt: new Date().toISOString(),
        }];
      }
    }
    // No net change (references + order preserved) → skip the write.
    if (next.length === entries.length && next.every((e, i) => e === entries[i])) return;
    try {
      await fetch(ROSTER_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        // base = the fresh full read above, so the server 3-way-merges this
        // sick-leave change instead of overwriting concurrent edits.
        body: JSON.stringify({ entries: next, events, base: { entries, events } }),
      });
    } catch { /* best-effort — the authorisation still stands */ }
  };

  const authorise = async (driverLogin, theReason, theNote, toStanding = false) => {
    setSaving(true);
    try {
      const res = await fetch(APPS_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'authoriseNoLogin', date, login: driverLogin, reason: theReason, note: theNote, by: login, standing: toStanding === true }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.error || 'Save failed');
      // Mirror a sick authorisation into the Leave Calendar so it tallies on their
      // profile; clearing the auth (or switching to a non-leave reason) removes ours.
      await syncAbsenceLeave(driverLogin, REASON_LEAVE_TYPE[theReason] || null);
      setEditing(null); setNote(''); setStanding(false);
      await load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  // Undo an accidental log-off: reopen the driver's shift, keeping the original
  // start time. The driver then logs back in and resumes (and finishes properly).
  const reopen = async (driverLogin) => {
    if (!window.confirm(`Reopen ${driverLogin}'s shift?\n\nThis restores their original start time and removes the finished log so they can log back in, resume, and finish again.`)) return;
    setReopening(driverLogin); setErr('');
    try {
      const res = await fetch(APPS_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'reopenShift', date, login: driverLogin, by: login }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.error || 'Reopen failed');
      await load();
    } catch (e) { setErr(e.message); }
    finally { setReopening(''); }
  };

  // Start a shift for a driver who forgot to log on. Creates the active-shift
  // record (saveShiftSession) so they show ON_SHIFT here AND their portal picks it
  // up on next login (recoverActiveShift), letting them finish it normally.
  const startForDriver = async (driverLogin, name) => {
    setStartBusy(true); setErr('');
    try {
      const iso = new Date(`${date}T${startTime || nowHHMM()}`).toISOString();
      const res = await fetch(APPS_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'saveShiftSession', driver: driverLogin, date, shiftStart: iso, shiftState: 'on_shift', name: name || driverLogin, odoStart: startKm !== '' ? Number(startKm) : '' }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.error || 'Could not start shift');
      setStarting(null);
      await load();
    } catch (e) { setErr(e.message); }
    finally { setStartBusy(false); }
  };

  // Add / remove a driver from the standing "Not Rostered" list.
  const toggleNotRostered = async (driverLogin, enabled) => {
    setErr('');
    try {
      const res = await fetch('/.netlify/functions/not-rostered-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'setNotRostered', login: driverLogin, enabled, by: login }),
      });
      const d = await res.json();
      if (d.success) setNotRosteredList(Array.isArray(d.list) ? d.list : []);
      else throw new Error(d.error || 'Could not update the Not Rostered list');
    } catch (e) { setErr(e.message); }
  };

  const rows      = data?.rows || [];
  const drivers   = rows.filter(r => !r.isStaff);
  const loggedOn  = drivers.filter(r => r.status !== 'NOT_LOGGED_ON');
  const notLogged = drivers.filter(r => r.status === 'NOT_LOGGED_ON');
  const notRosteredSet = new Set(notRostered.map(x => (x.login || '').toUpperCase()));
  // A standing "Not Rostered" driver who has logged on most recent weekdays is a regular
  // now, so they are NOT parked: a missed day shows in "needs a reason" like anyone else.
  // (PENAB sat on the list for 5½ weeks of full-time work and a lost start went unseen.)
  const recent = data?.recent || { window: 5, counts: {} };
  const REGULAR_DAYS = 3;
  const recentDays = (lg) => recent.counts?.[lg] || 0;
  const isRegular  = (lg) => recentDays(lg) >= REGULAR_DAYS;
  const parkedNR   = (lg) => notRosteredSet.has(lg) && !isRegular(lg);
  // Approved Leave Calendar entries covering this date → park those drivers out
  // of the "needs a reason" list (the calendar is the system of record for leave).
  const onLeaveMap = {};
  roster.forEach(e => {
    if ((e.status || '') !== 'approved') return;
    const s = (e.startDate || ''), en = (e.endDate || '');
    if (!s || !en || s > date || en < date) return;
    const lg = (e.login || '').trim().toUpperCase();
    if (lg) onLeaveMap[lg] = { type: e.type || 'annual', startDate: s, endDate: en };
  });
  const onLeave     = notLogged.filter(r => onLeaveMap[r.login]);
  // Standing "Not Rostered" drivers are parked out of the "needs a reason" list.
  const outstanding = notLogged.filter(r => !r.auth && !onLeaveMap[r.login] && !parkedNR(r.login));
  const authorised  = notLogged.filter(r => r.auth && !onLeaveMap[r.login] && !parkedNR(r.login));
  // The standing list, each annotated with today's status (came in / parked).
  const notRosteredRows = notRostered.map(x => {
    const lg = (x.login || '').toUpperCase();
    const row = rows.find(r => r.login === lg);
    return { login: lg, name: row?.name || lg, area: row?.area || '', loggedOn: !!row && row.status !== 'NOT_LOGGED_ON', regular: isRegular(lg), days: recentDays(lg) };
  }).sort((a, b) => (b.regular ? 1 : 0) - (a.regular ? 1 : 0) || (a.login < b.login ? -1 : 1));
  const regularsOnList = notRosteredRows.filter(r => r.regular);
  // All admins / team leaders (staff) with their status — shown in their own
  // section so they're visible without cluttering the driver "needs a reason" flow.
  const staff = rows.filter(r => r.isStaff)
    .sort((a, b) => (a.status === 'NOT_LOGGED_ON' ? 1 : 0) - (b.status === 'NOT_LOGGED_ON' ? 1 : 0) || (a.login < b.login ? -1 : 1));
  const staffOnCount = staff.filter(r => r.status !== 'NOT_LOGGED_ON').length;

  const card = { background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', padding: 20, marginBottom: 16 };
  const pill = (bg, bd) => ({ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '10px 14px', background: bg, border: `1px solid ${bd}`, borderRadius: 8, marginBottom: 8, flexWrap: 'wrap' });
  const sectionHead = { fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 };
  const chip = (label, value, color) => (
    <div style={{ ...card, marginBottom: 0, textAlign: 'center', padding: 16 }}>
      <div style={{ fontSize: '0.62rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
      <div style={{ fontSize: '1.8rem', fontWeight: 800, color }}>{value}</div>
    </div>
  );
  // Inline "start this driver's shift" editor — shared by the outstanding list
  // and the on-leave "Activate today" action.
  const startEditor = (r) => (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', width: '100%', marginTop: 4 }}>
      <span style={{ fontSize: '0.74rem', color: 'var(--hp-dim)' }}>Start time</span>
      <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
        style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 6, padding: '6px 8px', color: 'var(--hp-text)', fontSize: '0.78rem' }} />
      <span style={{ fontSize: '0.74rem', color: 'var(--hp-dim)' }}>Start Kms</span>
      <input type="number" inputMode="numeric" placeholder="odometer" value={startKm} onChange={e => setStartKm(e.target.value)}
        style={{ width: 110, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 6, padding: '6px 8px', color: 'var(--hp-text)', fontSize: '0.78rem' }} />
      <button disabled={startBusy} onClick={() => startForDriver(r.login, r.name)} style={{
        background: 'var(--hp-green)', border: 'none', color: '#0b1220', borderRadius: 6,
        padding: '6px 14px', fontWeight: 700, fontSize: '0.76rem', cursor: startBusy ? 'wait' : 'pointer',
      }}>
        {startBusy ? 'Starting…' : 'Start shift'}
      </button>
      <button onClick={() => setStarting(null)} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', padding: 4 }}>
        <X size={15} />
      </button>
      <span style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', flexBasis: '100%' }}>
        They'll appear logged on, and their portal will pick up the running shift next time they open it.
      </span>
    </div>
  );

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '4px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--hp-gold)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <LogIn size={22} /> Log-On Check
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 3 }}>
            Portal log-on is the true start of shift — who has and hasn't logged on, and why
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="date" value={date} onChange={e => setDate(e.target.value)}
            style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 7, padding: '7px 10px', color: 'var(--hp-text)', fontSize: '0.82rem' }}
          />
          <button onClick={load} disabled={loading} style={{
            background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.35)', color: 'var(--hp-gold)',
            borderRadius: 7, padding: '7px 14px', fontWeight: 600, fontSize: '0.8rem', cursor: loading ? 'wait' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <RefreshCw size={14} style={loading ? { animation: 'spin 0.8s linear infinite' } : undefined} /> Refresh
          </button>
        </div>
      </div>

      {loading && !data && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--hp-dim)' }}>
          <div style={{ width: 36, height: 36, border: '3px solid var(--hp-border)', borderTopColor: 'var(--hp-gold)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          Checking…
        </div>
      )}
      {err && (
        <div style={{ ...card, color: 'var(--hp-red)', display: 'flex', gap: 10, alignItems: 'center' }}>
          <AlertCircle size={16} /> {err}
        </div>
      )}

      {data && (
        <>
          {/* Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(140px, 100%), 1fr))', gap: 12, marginBottom: 16 }}>
            {chip('Expected', drivers.length, 'var(--hp-gold)')}
            {chip('Logged on', loggedOn.length, 'var(--hp-green)')}
            {chip('Not logged on', outstanding.length, outstanding.length ? 'var(--hp-red)' : 'var(--hp-green)')}
            {onLeave.length > 0 && chip('On leave', onLeave.length, '#a78bfa')}
            {notRosteredRows.length > 0 && chip('Not rostered', notRosteredRows.length, 'var(--hp-dim)')}
            {chip('Authorised', authorised.length, '#60a5fa')}
          </div>

          {/* NOT logged on — outstanding (the list that matters) */}
          <div style={card}>
            <div style={sectionHead}>Not logged on — needs a reason ({outstanding.length})</div>
            {outstanding.length === 0 ? (
              <div style={{ fontSize: '0.85rem', color: 'var(--hp-green)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircle size={15} /> Every expected driver has logged on or been authorised.
              </div>
            ) : outstanding.map(r => (
              <div key={r.login} style={pill('rgba(239,68,68,0.07)', 'rgba(239,68,68,0.3)')}>
                <span style={{ fontSize: '0.88rem', color: 'var(--hp-text)' }}>
                  <strong>{r.login}</strong>{r.name && r.name !== r.login ? ` · ${r.name}` : ''}
                  {r.area ? <span style={{ color: 'var(--hp-dim)', fontSize: '0.74rem' }}> · {r.area}</span> : ''}
                  {notRosteredSet.has(r.login) && (
                    <span style={{ color: 'var(--hp-amber)', fontSize: '0.74rem', fontWeight: 700 }}> · on the Not Rostered list, but logged on {recentDays(r.login)} of the last {recent.window} weekdays</span>
                  )}
                </span>
                {canAuthorise && editing !== r.login && starting !== r.login && (
                  <span style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => { setStarting(r.login); setStartTime(nowHHMM()); setStartKm(''); }} title="Start this driver's shift (they forgot to log on)" style={{
                      background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.45)', color: 'var(--hp-green)',
                      borderRadius: 6, padding: '5px 12px', fontWeight: 600, fontSize: '0.74rem', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
                    }}>
                      <LogIn size={13} /> Start shift
                    </button>
                    <button onClick={() => { setEditing(r.login); setReason(REASONS[0]); setNote(''); setStanding(false); }} style={{
                      background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.4)', color: '#60a5fa',
                      borderRadius: 6, padding: '5px 12px', fontWeight: 600, fontSize: '0.74rem', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 5,
                    }}>
                      <ShieldCheck size={13} /> Authorise
                    </button>
                  </span>
                )}
                {starting === r.login && startEditor(r)}
                {editing === r.login && (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', width: '100%', marginTop: 4 }}>
                    <select value={reason} onChange={e => setReason(e.target.value)} style={{
                      background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 6,
                      padding: '6px 8px', color: 'var(--hp-text)', fontSize: '0.78rem', flex: '1 1 160px',
                    }}>
                      {REASONS.map(x => <option key={x} value={x}>{x}</option>)}
                    </select>
                    <input
                      placeholder="Note (optional)" value={note} onChange={e => setNote(e.target.value)}
                      style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 6, padding: '6px 8px', color: 'var(--hp-text)', fontSize: '0.78rem', flex: '2 1 180px' }}
                    />
                    {reason === 'Not Rostered' && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.74rem', color: 'var(--hp-dim)', flexBasis: '100%', order: 5, cursor: 'pointer' }}>
                        <input type="checkbox" checked={standing} onChange={e => setStanding(e.target.checked)} />
                        Also add to the standing Not Rostered list: they don't usually log on, so stop asking on future days.
                        Leave unticked for a one-off day (e.g. a new starter who hasn't started yet).
                      </label>
                    )}
                    <button disabled={saving} onClick={() => authorise(r.login, reason, note, reason === 'Not Rostered' && standing)} style={{
                      background: 'var(--hp-green)', border: 'none', color: '#0b1220', borderRadius: 6,
                      padding: '6px 14px', fontWeight: 700, fontSize: '0.76rem', cursor: saving ? 'wait' : 'pointer',
                    }}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button onClick={() => setEditing(null)} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', padding: 4 }}>
                      <X size={15} />
                    </button>
                  </div>
                )}
              </div>
            ))}
            {!canAuthorise && outstanding.length > 0 && (
              <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 4 }}>
                Authorising is limited to {AUTHORISERS.join(', ')}.
              </div>
            )}
          </div>

          {/* On leave — parked from the Leave Calendar */}
          {onLeave.length > 0 && (
            <div style={card}>
              <div style={sectionHead}>On leave — parked from the roster ({onLeave.length})</div>
              {onLeave.map(r => {
                const lv = onLeaveMap[r.login];
                const kind = leaveKind(lv.type);
                const period = lv.startDate === lv.endDate
                  ? (lv.startDate === date ? 'today' : `on ${fmtDay(lv.startDate)}`)
                  : `until ${fmtDay(lv.endDate)}`;
                const sick = lv.type === 'sick';
                return (
                  <div key={r.login} style={pill('rgba(167,139,250,0.07)', 'rgba(167,139,250,0.3)')}>
                    <span style={{ fontSize: '0.88rem', color: 'var(--hp-text)' }}>
                      <strong>{r.login}</strong>{r.name && r.name !== r.login ? ` · ${r.name}` : ''}
                      {r.area ? <span style={{ color: 'var(--hp-dim)', fontSize: '0.74rem' }}> · {r.area}</span> : ''}
                    </span>
                    {starting === r.login ? startEditor(r) : (
                      <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.76rem', fontWeight: 700, color: sick ? 'var(--hp-amber)' : '#a78bfa' }}>
                          {sick ? '🤒 ' : ''}{kind} · {period}
                        </span>
                        {canAuthorise && (
                          <button onClick={() => { setStarting(r.login); setStartTime(nowHHMM()); setStartKm(''); }}
                            title="They came in despite leave — start their shift for today" style={{
                              background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.45)', color: 'var(--hp-green)',
                              borderRadius: 6, padding: '5px 12px', fontWeight: 600, fontSize: '0.74rem', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
                            }}>
                            <LogIn size={13} /> Activate today
                          </button>
                        )}
                      </span>
                    )}
                  </div>
                );
              })}
              <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 4 }}>
                Pulled from the Leave Calendar (approved leave covering this date). "Activate today" starts their shift if they came in anyway.
              </div>
            </div>
          )}

          {/* Not Rostered — standing list of usual non-loggers (parked) */}
          <div style={card}>
            <div style={sectionHead}>Not rostered — usual non-loggers ({notRosteredRows.length})</div>
            {regularsOnList.length > 0 && (
              <div style={{ fontSize: '0.8rem', color: 'var(--hp-text)', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
                <strong>{regularsOnList.length === 1 ? '1 driver on this list logs' : `${regularsOnList.length} drivers on this list log`} on most days now.</strong>{' '}
                They're no longer parked: a day they don't log on shows under “needs a reason”. Remove them from the list if they're regulars.
              </div>
            )}
            {notRosteredRows.length === 0 ? (
              <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>
                None yet. Add drivers who don't usually log on below, or tick “add to the standing list” when authorising someone as “Not Rostered”.
              </div>
            ) : notRosteredRows.map(r => (
              <div key={r.login} style={r.regular ? pill('rgba(245,158,11,0.07)', 'rgba(245,158,11,0.35)') : pill('rgba(148,163,184,0.08)', 'var(--hp-border)')}>
                <span style={{ fontSize: '0.88rem', color: 'var(--hp-text)' }}>
                  <strong>{r.login}</strong>{r.name && r.name !== r.login ? ` · ${r.name}` : ''}
                  {r.area ? <span style={{ color: 'var(--hp-dim)', fontSize: '0.74rem' }}> · {r.area}</span> : ''}
                  {r.loggedOn && <span style={{ color: 'var(--hp-green)', fontSize: '0.74rem', fontWeight: 700 }}> · came in today</span>}
                  {r.regular && <span style={{ color: 'var(--hp-amber)', fontSize: '0.74rem', fontWeight: 700 }}> · logged on {r.days} of the last {recent.window} weekdays, not parked</span>}
                </span>
                {canAuthorise && r.regular && (
                  <button onClick={() => toggleNotRostered(r.login, false)} style={{
                    background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.45)', color: 'var(--hp-amber)',
                    borderRadius: 6, padding: '5px 12px', fontWeight: 600, fontSize: '0.74rem', cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>Remove from list</button>
                )}
                {canAuthorise && !r.regular && (
                  <button title="Remove from the Not Rostered list" onClick={() => toggleNotRostered(r.login, false)}
                    style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', padding: 2 }}>
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
            {canAuthorise && (
              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                <select value={addNR} onChange={e => setAddNR(e.target.value)} style={{
                  background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 6,
                  padding: '6px 8px', color: 'var(--hp-text)', fontSize: '0.78rem', flex: '1 1 200px',
                }}>
                  <option value="">+ Add a driver to the list…</option>
                  {drivers.filter(d => !notRosteredSet.has(d.login)).map(d => (
                    <option key={d.login} value={d.login}>{d.login}{d.name && d.name !== d.login ? ` · ${d.name}` : ''}</option>
                  ))}
                </select>
                <button disabled={!addNR} onClick={() => { toggleNotRostered(addNR, true); setAddNR(''); }} style={{
                  background: 'var(--hp-green)', border: 'none', color: '#0b1220', borderRadius: 6,
                  padding: '6px 16px', fontWeight: 700, fontSize: '0.76rem', cursor: addNR ? 'pointer' : 'default', opacity: addNR ? 1 : 0.5,
                }}>Add</button>
              </div>
            )}
            <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 6 }}>
              Standing list of drivers who don't usually log on, parked out of “needs a reason”. Anyone who logs on {REGULAR_DAYS} or more of the last {recent.window} weekdays isn't parked, so the list can't hide a regular.
            </div>
          </div>

          {/* Authorised no-log-ons */}
          {authorised.length > 0 && (
            <div style={card}>
              <div style={sectionHead}>Authorised — not logging on today ({authorised.length})</div>
              {authorised.map(r => (
                <div key={r.login} style={pill('rgba(96,165,250,0.07)', 'rgba(96,165,250,0.25)')}>
                  <span style={{ fontSize: '0.88rem', color: 'var(--hp-text)' }}>
                    <strong>{r.login}</strong>{r.name && r.name !== r.login ? ` · ${r.name}` : ''}
                  </span>
                  <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: '0.76rem', color: '#60a5fa', fontWeight: 700 }}>
                      {r.auth.reason}{r.auth.note ? ` — ${r.auth.note}` : ''}
                      <span style={{ color: 'var(--hp-dim)', fontWeight: 500 }}> · by {r.auth.by}</span>
                    </span>
                    {canAuthorise && REASON_LEAVE_TYPE[r.auth.reason] && (
                      <button
                        title="Record this as a sick day on the Leave Calendar so it tallies on their profile"
                        disabled={syncing === r.login}
                        onClick={async () => {
                          setSyncing(r.login); setErr('');
                          try { await syncAbsenceLeave(r.login, REASON_LEAVE_TYPE[r.auth.reason]); await load(); }
                          catch (e) { setErr(e.message); }
                          finally { setSyncing(''); }
                        }}
                        style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.45)', color: 'var(--hp-amber)', borderRadius: 6, padding: '4px 10px', fontWeight: 700, fontSize: '0.72rem', cursor: syncing === r.login ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
                        {syncing === r.login ? 'Adding…' : '＋ Tally sick day'}
                      </button>
                    )}
                    {canAuthorise && (
                      <button title="Clear authorisation" onClick={() => authorise(r.login, '', '')} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', padding: 2 }}>
                        <X size={14} />
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Logged on — true start times */}
          <div style={card}>
            <div style={sectionHead}>Logged on — true start times ({loggedOn.length})</div>
            {loggedOn.length === 0 ? (
              <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>No portal log-ons yet for this date.</div>
            ) : [...loggedOn].sort((a, b) => (a.shiftStart || '99') < (b.shiftStart || '99') ? -1 : 1).map(r => (
              <div key={r.login} style={pill('rgba(52,211,153,0.07)', 'rgba(52,211,153,0.25)')}>
                <span style={{ fontSize: '0.88rem', color: 'var(--hp-text)' }}>
                  <strong>{r.login}</strong>{r.name && r.name !== r.login ? ` · ${r.name}` : ''}
                  {r.area ? <span style={{ color: 'var(--hp-dim)', fontSize: '0.74rem' }}> · {r.area}</span> : ''}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 700, fontFamily: 'var(--font-mono, monospace)', color: 'var(--hp-green)' }}>
                    {r.shiftStart || '—'}
                    {r.status === 'FINISHED'
                      ? <span style={{ color: 'var(--hp-dim)', fontWeight: 600 }}> → {r.finishTime}</span>
                      : <span style={{ color: 'var(--hp-amber)', fontWeight: 600 }}> · {r.state === 'on_break' ? 'on break' : 'on shift'}</span>}
                  </span>
                  {r.status === 'FINISHED' && canAuthorise && (
                    <button
                      onClick={() => reopen(r.login)}
                      disabled={reopening === r.login}
                      title="Undo an accidental log-off — restores the shift with the original start time"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', background: 'var(--hp-surface)', border: '1px solid var(--hp-gold)', borderRadius: 6, color: 'var(--hp-gold)', fontWeight: 700, fontSize: '0.72rem', cursor: reopening === r.login ? 'default' : 'pointer', whiteSpace: 'nowrap' }}
                    >
                      <RefreshCw size={12} /> {reopening === r.login ? 'Reopening…' : 'Reopen'}
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>

          {/* Team Leaders & Admin — shown with their log-on status */}
          {staff.length > 0 && (
            <div style={card}>
              <div style={sectionHead}>Team Leaders & Admin — {staffOnCount}/{staff.length} logged on</div>
              {staff.map(r => {
                const on = r.status !== 'NOT_LOGGED_ON';
                return (
                  <div key={r.login} style={pill(on ? 'rgba(52,211,153,0.07)' : 'rgba(148,163,184,0.08)', on ? 'rgba(52,211,153,0.25)' : 'var(--hp-border)')}>
                    <span style={{ fontSize: '0.88rem', color: 'var(--hp-text)' }}>
                      <strong>{r.login}</strong>{r.name && r.name !== r.login ? ` · ${r.name}` : ''}
                      {r.area ? <span style={{ color: 'var(--hp-dim)', fontSize: '0.74rem' }}> · {r.area}</span> : ''}
                    </span>
                    {on ? (
                      <span style={{ fontSize: '0.8rem', fontWeight: 700, fontFamily: 'var(--font-mono, monospace)', color: 'var(--hp-green)' }}>
                        {r.shiftStart || '—'}
                        {r.status === 'FINISHED'
                          ? <span style={{ color: 'var(--hp-dim)', fontWeight: 600 }}> → {r.finishTime}</span>
                          : <span style={{ color: 'var(--hp-amber)', fontWeight: 600 }}> · {r.state === 'on_break' ? 'on break' : 'on shift'}</span>}
                      </span>
                    ) : (
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--hp-dim)' }}>Not logged on</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
